// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {PropertyType} from 'common/report/constants';
import {Segmentation} from 'common/types/reports/query';
import {ENTER_KEY_CODE} from 'common/util/dom';
import type {Button} from 'common/components/button';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';

// eslint-disable-next-line import/no-cycle
import {template} from './template';
import css from './index.inline.styl';

interface BucketPaneState {
  editedBucket: Bucket; // needed for the non-prop based version of bucketing
  validBucket: boolean;
  segment: Segmentation;
}

interface BucketPaneJsxProps {
  attrs: {
    'event-names'?: string[];
    units?: string[] | Array<{value: string; label: string}>;
    'selected-bucket'?: Bucket;
    'selected-unit'?: string;
    'reset-bucket'?: boolean;
    'max-num-buckets'?: number;
    'range-min'?: number;
    'units-as-property'?: boolean;
    header?: string;
  };
  on?: {
    close?: (ev: CustomEvent) => void;
    changeSelectedBucket?: (ev: CustomEvent) => void;
    updateSegment?: (ev: CustomEvent) => void;
    resetBucket?: (ev: CustomEvent) => void;
  };
  props?: {
    segment?: Segmentation;
  };
}

/** widget for selecting bucket size, min, and max. */
export class BucketPane extends Component<
  BucketPaneState,
  BucketPaneJsxProps['attrs'],
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<BucketPaneJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed prop values belong only to the set of events passed`,
      },
      units: {
        type: `json`,
        description: `interface: string[]`,
      },
      'selected-bucket': {type: `json`, description: `Currently selected bucket`},
      'selected-unit': {type: `string`, description: `Currently selected unit`},
      'reset-bucket': {
        type: `boolean`,
        description: `whether to display reset button`,
      },
      'max-num-buckets': {
        type: `number`,
        default: 90,
        description: `provide a max number of buckets allowed for error handling`,
      },
      'range-min': {
        type: `number`,
        default: null,
        description: `if specified, the minimum value needed to pass validation`,
      },
      header: {
        type: `string`,
        default: `Define Range`,
        description: `header text`,
      },
      'units-as-property': {
        type: `boolean`,
        description: `if set to true, will set the Units value on the bucket, instead of adjusting Min/Max`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        editedBucket: null,
        validBucket: true,
        segment: null,
      },
    };
  }

  private _submitButton: Button = null;

  applyBucketSelections() {
    const customBucket =
      !this.state.segment ||
      this.state.segment.propertyType === PropertyType.Number ||
      this.state.segment.listItemGroup?.propertyType === PropertyType.Number
        ? this.state.editedBucket ?? this.attr(`selected-bucket`)
        : undefined;
    // TODO can consolidate once we remove legacy usages
    this.state.segment
      ? this.dispatchEvent(
          new CustomEvent(`updateSegment`, {
            detail: {
              segment: {
                ...this.state.segment,
                customBucket,
              },
            },
          }),
        )
      : this.dispatchEvent(
          new CustomEvent(`changeSelectedBucket`, {
            detail: {
              ...customBucket,
            },
          }),
        );
  }
  resetInterval() {
    this.state.segment
      ? this.dispatchEvent(
          new CustomEvent(`updateSegment`, {
            detail: {
              segment: {...this.state.segment, customBucket: undefined, customGroups: undefined},
            },
          }),
        )
      : this.dispatchEvent(new CustomEvent(`resetBucket`, {detail: null}));
  }
  submitDisabled() {
    return !this.state.validBucket;
  }
  handleKeyUp(ev) {
    if (ev.keyCode === ENTER_KEY_CODE && !this.submitDisabled()) {
      this.applyBucketSelections();
    }
  }
  close() {
    this.dispatchEvent(new CustomEvent(`close`));
  }

  getPropertyUnit() {
    const unit = this.state.segment.unit;
    if (unit === TimeUnit.Minute || unit === TimeUnit.Session) {
      throw new Error(`${unit} time buckets are not allowed`);
    }
    return unit;
  }

  handleUnitUpdate({unit, isValid, focusSubmit}) {
    focusSubmit && this._submitButton.focus();
    this.update({
      segment: {...this.state.segment, unit},
      validBucket: isValid,
    });
  }

  handleBucketUpdate({bucket, isValid}) {
    this.update({editedBucket: bucket, validBucket: isValid});
  }

  handleSegmentUpdate({
    segment,
    isValid,
    focusSubmit,
  }: {
    segment: Segmentation;
    isValid: boolean;
    focusSubmit: boolean;
  }) {
    focusSubmit && this._submitButton.focus();
    if (this.state.segment.listItemGroup) {
      // For list of objects, the segment passed to `StringBucketPaneEl` is segment.listItemGroup, so
      // we use the emitted segment to update segment.listItemGroup
      const {customGroups, ...listItemGroup} = segment;
      segment = {...this.state.segment, listItemGroup, customGroups};
    }
    this.update({segment, validBucket: isValid});
  }

  getSegmentParams() {
    const segment = this.state.segment;
    let params: {segment: Segmentation; listProperty?: Segmentation} = {segment};

    if (segment.listItemGroup) {
      // For list of objects, we pass segment.listItemGroup as the segment (and include the outer customGroups).
      // listProperty is also needed so the appropriate values query can be made using the listProperty
      params = {
        segment: {
          ...segment.listItemGroup,
          ...(segment.customGroups ? {customGroups: segment.customGroups} : {}),
        },
        listProperty: segment,
      };
    }

    return params;
  }

  handleSubmitInsert(el: Button) {
    this._submitButton = el;

    // Focus Apply button except for numeric since you can submit from the input box
    if (this.state.segment.propertyType !== PropertyType.Number) {
      // Need two animation frames since this modal usually appears on click which pulls focus
      requestAnimationFrame(() => requestAnimationFrame(() => this._submitButton.focus()));
    }
  }

  set segment(segment: Segmentation) {
    this.update({segment});
  }
}

export const BucketPaneEl = defineAndMakeJsxFactory<BucketPaneJsxProps>(`mp-bucket-pane`, BucketPane);
export const BucketPaneReact = panel2react<BucketPaneJsxProps>(`mp-bucket-pane`, BucketPane);
